import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import AutoBookPromoteBanner from "@/components/AutoBookPromoteBanner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchSelect from "@/components/common/SearchSelect";
import { QuickAddItemDialog } from "@/components/QuickAddItemDialog";
import { LastRateHint } from "@/components/LastRateHint";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Download, Upload as UploadIcon, ArrowRight, Send } from "lucide-react";
import { downloadBlob } from "@/lib/download";
import { API } from "@/lib/api";
import Attachments from "@/components/Attachments";
import AttachButton from "@/components/AttachButton";
import StagedAttachments from "@/components/StagedAttachments";
import DSCSigningPanel from "@/components/DSCSigningPanel";
import ESignButton from "@/components/esign/ESignButton";
import { Switch } from "@/components/ui/switch";
import SendReportButton from "@/components/common/SendReportButton";
import RcmAdvisoryModal from "@/components/purchases/RcmAdvisoryModal";
import GstinSelector from "@/components/gst/GstinSelector";
import { exportXlsx } from "@/lib/xlsx-export";
import { CancelInvoiceDialog } from "@/components/sales/CancelInvoiceDialog";
import { tdsOption } from "@/lib/tdsLabel";
import { DraftsPanel, saveDraft, discardDraft } from "@/components/DraftsPanel";
import { DocNumberField } from "@/components/sales/InvoiceNumberField";

export default function Purchases() {
  const stagedBillRef = React.useRef();
  const stagedPORef = React.useRef();
  const [bills, setBills] = useState([]);
  const [pos, setPOs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [quickLine, setQuickLine] = useState(null);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [openBill, setOpenBill] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftsKey, setDraftsKey] = useState(0);
  const [openPO, setOpenPO] = useState(false);
  const [openBillDetail, setOpenBillDetail] = useState(null);
  const [showBillCancel, setShowBillCancel] = useState(false);
  const [canCancelBill, setCanCancelBill] = useState(false);
  useEffect(() => { api.get("/purchase/bill-cancel-permission").then((r) => setCanCancelBill(!!r.data.allowed)).catch(() => {}); }, []);
  const [openPoDetail, setOpenPoDetail] = useState(null);
  const [tdsSections, setTdsSections] = useState([]);
  const [form, setForm] = useState({ vendor_id: "", date: today(), branch_id: "", vendor_bill_number: "",
    entity_gstin: "",   // company-side GSTIN receiving this bill
    department_id: "",  // Iter-106
    auto_tds: false, tds_section: "", tds_rate: 10,
    lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] });
  const [topGstinFilter, setTopGstinFilter] = useState("");

  const load = async () => {
    setBills((await api.get("/purchase/bills")).data);
    setPOs((await api.get("/purchase/orders")).data);
  };
  useEffect(() => {
    load();
    api.get("/parties", { params: { party_type: "vendor" } }).then((r) => setVendors(r.data));
    api.get("/items").then((r) => setItems(r.data));
    api.get("/branches").then((r) => { setBranches(r.data); setForm((f) => ({ ...f, branch_id: r.data[0]?.id || "" })); });
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    api.get("/tds/sections").then((r) => setTdsSections(r.data || [])).catch(() => {});
  }, []);

  const convertPOtoBill = async (po) => {
    try {
      const r = await api.post(`/purchase/orders/${po.id}/convert-bill`);
      toast.success(`PO ${po.po_number} → Bill ${r.data.bill.bill_number} posted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setItem = (i, itemId, obj) => {
    const it = obj || items.find((x) => x.id === itemId);
    if (!it) return;
    setForm((f) => ({ ...f, lines: f.lines.map((ln, j) => j === i ? {
      ...ln, item_id: itemId, description: it.name, hsn_code: it.hsn_code || "", rate: it.purchase_price, gst_rate: it.gst_rate,
    } : ln) }));
  };

  const compute = (l) => {
    const base = Number(l.qty) * Number(l.rate) * (1 - Number(l.discount_pct || 0) / 100);
    return { base, tax: base * Number(l.gst_rate) / 100, total: base * (1 + Number(l.gst_rate) / 100) };
  };
  const totals = form.lines.reduce((a, l) => { const c = compute(l); return { sub: a.sub + c.base, tax: a.tax + c.tax, grand: a.grand + c.total }; }, { sub: 0, tax: 0, grand: 0 });

  const submit = async (kind) => {
    if (!form.vendor_id) return toast.error("Select a vendor");
    try {
      const url = kind === "bill" ? "/purchase/bills" : "/purchase/orders";
      const res = await api.post(url, form);
      const docType = kind === "bill" ? "purchase_bill" : "purchase_order";
      const ref = kind === "bill" ? stagedBillRef : stagedPORef;
      await ref.current?.flush(docType, res.data.id);
      if (kind === "bill") { await discardDraft(draftId); setDraftId(null); setDraftsKey((k) => k + 1); }
      toast.success(kind === "bill" ? "Bill posted" : "PO created");
      setOpenBill(false); setOpenPO(false);
      setForm((f) => ({ ...f, vendor_id: "", vendor_bill_number: "", bill_number: "", lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] }));
      load();
      // Fire the RCM / Sec 17(5) compliance advisory modal for every new bill —
      // the user can review and confirm (or skip) the RCM/reversal postings.
      if (kind === "bill" && res.data?.id) setAdvisoryBillId(res.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approvePO = async (id) => {
    try { await api.post(`/purchase/orders/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPDF = async (kind, id) => {
    const path = kind === "bill" ? `/purchase/bills/${id}/pdf` : `/purchase/orders/${id}/pdf`;
    await downloadBlob(path, `${kind}-${id.slice(0,8)}.pdf`);
  };

  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [advisoryBillId, setAdvisoryBillId] = useState(null);   // RCM/17(5) modal trigger
  const extractBill = async (file) => {
    if (!file) return;
    setExtracting(true); setExtractResult(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject; r.readAsDataURL(file);
      });
      const r = await api.post("/ai/extract-bill", { image_base64: base64, mime_type: file.type });
      setExtractResult(r.data);
      // Auto-fill form
      const matchedVendor = vendors.find((v) =>
        v.name.toLowerCase().includes((r.data.vendor_name || "").toLowerCase().split(" ")[0]) ||
        (r.data.vendor_gstin && v.gstin === r.data.vendor_gstin)
      );
      setForm((f) => ({
        ...f,
        vendor_id: matchedVendor?.id || f.vendor_id,
        vendor_bill_number: r.data.bill_number || "",
        date: r.data.date || f.date,
        lines: (r.data.lines && r.data.lines.length) ? r.data.lines.map((l) => ({
          item_id: "",
          description: l.description || "",
          hsn_code: l.hsn_code || "",
          qty: Number(l.qty) || 1,
          rate: Number(l.rate) || 0,
          gst_rate: Number(l.gst_rate) || 18,
        })) : f.lines,
      }));
      toast.success(`Extracted — confidence ${Math.round((r.data.confidence || 0) * 100)}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Extraction failed"); }
    finally { setExtracting(false); }
  };

  const Form = ({ kind }) => (
    <div>
      {kind === "bill" && (
        <div className="mt-4 border border-accent/40 bg-accent/5 rounded-sm p-4 space-y-3" data-testid="ai-extract-block">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent" />
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent">AI Bill Extraction · Gemini 3 Flash</div>
          </div>
          <div className="text-xs text-muted-foreground">Upload a photo or PDF-page image of the vendor bill — Gemini will read it and pre-fill the form.</div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-2 h-9 px-3 border rounded-sm bg-card text-sm hover:bg-muted" data-testid="ai-extract-upload">
              <UploadIcon className="w-3.5 h-3.5" />
              {extracting ? "Extracting…" : "Upload bill image"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                     onChange={(e) => e.target.files?.[0] && extractBill(e.target.files[0])} disabled={extracting} />
            </label>
            {extractResult && (
              <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                <span>Vendor: <strong className="text-foreground">{extractResult.vendor_name || "—"}</strong></span>
                <span>Bill: <strong className="font-mono">{extractResult.bill_number || "—"}</strong></span>
                <span>Total: <strong className="font-mono">{extractResult.grand_total ? `₹${Number(extractResult.grand_total).toLocaleString("en-IN")}` : "—"}</strong></span>
                <span className="text-[10px] font-mono uppercase text-accent">conf {Math.round((extractResult.confidence || 0) * 100)}%</span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        {kind === "bill" && (
          <DocNumberField kind="purchase_bill" label="Bill No. (ours)" open={openBill} branchId={form.branch_id}
            value={form.bill_number} maxLength={30} testId="bill-number"
            onChange={(v) => setForm((f) => ({ ...f, bill_number: v }))} />
        )}
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-widest">Vendor</Label>
          <SearchSelect
            options={vendors.map((c) => ({ value: c.id, label: c.name, sublabel: c.gstin }))}
            value={form.vendor_id}
            onChange={(v) => setForm({ ...form, vendor_id: v })}
            testId={`${kind}-vendor`}
            placeholder="Select vendor" />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-widest">Branch</Label>
          <Select value={form.branch_id} onValueChange={(v) => {
            const br = branches.find((b) => b.id === v);
            setForm({ ...form, branch_id: v,
                         entity_gstin: br?.gstin || form.entity_gstin });
          }}>
            <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-widest">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" />
        </div>
        {departments.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-widest">Department</Label>
            <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid={`${kind}-department`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {kind === "bill" && (
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase font-mono tracking-widest">Vendor Bill #</Label>
            <Input value={form.vendor_bill_number} onChange={(e) => setForm({ ...form, vendor_bill_number: e.target.value })} className="rounded-sm mt-1" placeholder="Vendor's invoice number" />
          </div>
        )}
      </div>
      <div className="mt-6 border rounded-sm overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th style={{ width: 220 }}>Item</th><th style={{ width: 90 }}>HSN</th><th style={{ width: 80 }} className="text-right">Qty</th><th style={{ width: 110 }} className="text-right">Rate</th><th style={{ width: 80 }} className="text-right">GST %</th><th className="text-right">Total</th><th></th></tr></thead>
          <tbody>
            {form.lines.map((l, i) => {
              const c = compute(l);
              return (
                <tr key={i}>
                  <td className="p-1 space-y-1">
                    <SearchSelect
                      options={[{ value: "__new__", label: "+ Create new item / service" }, { value: "custom", label: "— Custom (no item link) —" }, ...items.map((it) => ({ value: it.id, label: it.name, sublabel: it.sku }))]}
                      value={l.item_id || "custom"}
                      onChange={(v) => { if (v === "__new__") setQuickLine(i); else if (v !== "custom") setItem(i, v); }}
                      testId={`bill-item-${i}`}
                      placeholder="Pick item"
                      className="h-8 text-xs" />
                    {i === 0 && (
                      <QuickAddItemDialog open={quickLine !== null} onOpenChange={(o) => !o && setQuickLine(null)} side="purchase"
                        onCreated={(it) => { setItems((p) => [...p, it]); setItem(quickLine, it.id, it); }} />
                    )}
                    <Input value={l.description} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))} className="rounded-sm h-8 text-xs" data-testid={`bill-desc-${i}`} />
                    <LastRateHint itemId={l.item_id} partyId={form.vendor_id} side="purchase" currentRate={l.rate} testId={`bill-last-rate-${i}`}
                      onApply={(r) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, rate: r.rate } : x) }))} />
                  </td>
                  <td className="p-1"><Input value={l.hsn_code || ""} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, hsn_code: e.target.value } : x) }))} className="rounded-sm h-8 text-xs" data-testid={`bill-hsn-${i}`} /></td>
                  <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums" /></td>
                  <td className="p-1"><Input type="number" value={l.rate} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, rate: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums" data-testid={`bill-rate-${i}`} /></td>
                  <td className="p-1"><Input type="number" value={l.gst_rate} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, gst_rate: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums" /></td>
                  <td className="text-right tabular-nums text-sm">{fmtINR(c.total)}</td>
                  <td className="text-center">{form.lines.length > 1 && <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="border-t p-3 bg-muted/40 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] }))} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Add line</Button>
          <div className="flex gap-6 text-sm tabular-nums"><div>Subtotal: <span className="font-mono">{fmtINR(totals.sub)}</span></div><div>GST: <span className="font-mono">{fmtINR(totals.tax)}</span></div><div className="font-semibold">Grand: <span className="font-mono">{fmtINR(totals.grand)}</span></div></div>
        </div>

        {/* Feb 2026 · Live GST preview widget (Purchases mirror of Sales) */}
        <div className="mt-3 p-3 rounded-sm border border-accent/25 bg-accent/5" data-testid="bill-gst-preview">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-1.5">// Live tax breakup</div>
          {(() => {
            const byRate = {};
            let sub = 0;
            form.lines.forEach(l => {
              const line = (Number(l.qty) || 0) * (Number(l.rate) || 0);
              const gst  = Number(l.gst_rate) || 0;
              sub += line;
              if (!byRate[gst]) byRate[gst] = { taxable: 0, tax: 0 };
              byRate[gst].taxable += line;
              byRate[gst].tax     += line * gst / 100;
            });
            const orgStateCode = (form.entity_gstin || "").slice(0, 2);
            const vend  = (vendors || []).find(v => v.id === form.vendor_id) || {};
            const posCode = (vend.state_code || (vend.gstin || "").slice(0, 2) || orgStateCode);
            const isIntra = form.is_import ? false : (orgStateCode && posCode ? orgStateCode === posCode : true);
            const totalTax = Object.values(byRate).reduce((a, b) => a + b.tax, 0);
            return (
              <div className="text-[11px] space-y-1">
                {Object.keys(byRate).sort((a, b) => Number(a) - Number(b)).map(g => (
                  <div key={g} className="flex justify-between font-mono">
                    <span>@ {g}% on {fmtINR(byRate[g].taxable)}</span>
                    {isIntra
                      ? <span>Input CGST {fmtINR(byRate[g].tax / 2)} + SGST {fmtINR(byRate[g].tax / 2)}</span>
                      : <span>Input IGST {fmtINR(byRate[g].tax)}</span>}
                  </div>
                ))}
                <div className="flex justify-between font-mono pt-1 mt-1 border-t border-accent/20">
                  <span>Subtotal · ITC · Grand</span>
                  <span className="font-semibold">{fmtINR(sub)} · {fmtINR(totalTax)} · {fmtINR(sub + totalTax)}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {form.reverse_charge ? "RCM — self-invoice · pay & claim ITC" : (isIntra ? "Intra-state · CGST + SGST claim" : "Inter-state · IGST claim")}
                </div>

                {/* RCM auto-post preview — shows the reversed self-invoice leg */}
                {form.reverse_charge && totalTax > 0 && (
                  <div className="mt-2 pt-2 border-t border-accent/20" data-testid="bill-gst-rcm-preview">
                    <div className="text-[10px] font-mono uppercase text-amber-600 mb-1">// RCM · self-invoice leg (auto-post)</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-mono">
                      {Object.keys(byRate).sort((a, b) => Number(a) - Number(b)).map(g => (
                        <React.Fragment key={g}>
                          <div className="text-muted-foreground">Output @ {g}%</div>
                          <div className="text-right">
                            {isIntra
                              ? <>CGST-RCM {fmtINR(byRate[g].tax / 2)} + SGST-RCM {fmtINR(byRate[g].tax / 2)}</>
                              : <>IGST-RCM {fmtINR(byRate[g].tax)}</>}
                          </div>
                          <div className="text-muted-foreground">Input Credit (claim)</div>
                          <div className="text-right">
                            {isIntra
                              ? <>Input CGST {fmtINR(byRate[g].tax / 2)} + SGST {fmtINR(byRate[g].tax / 2)}</>
                              : <>Input IGST {fmtINR(byRate[g].tax)}</>}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">Net GST impact: <b>₹0</b> (output = input · ITC claim netted)</div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
      <div className="mt-6 border rounded-sm p-3 bg-muted/30 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">Auto-deduct TDS / Auto-pay TCS</Label>
            <div className="text-[10px] text-muted-foreground mt-0.5">Buyer deducts TDS → posts TDS Payable Cr · Vendor charges TCS → posts TCS Receivable Dr</div>
          </div>
          <Switch checked={form.auto_tds} onCheckedChange={(v) => setForm({ ...form, auto_tds: v })} data-testid={`${kind}-auto-tds`} />
        </div>
        {form.auto_tds && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px]">Section</Label>
              <div className="mt-1"><SearchSelect options={tdsSections.map(tdsOption)} wrap large contentClassName="w-[min(44rem,94vw)]"
                value={form.tds_section} placeholder="Type 393, code 1027, nature or old 194J" testId={`${kind}-tds-section`} className="h-10 text-sm"
                onChange={(v) => { const s = tdsSections.find((x) => x.section === v); setForm((f) => ({ ...f, tds_section: v, tds_rate: s?.rate ?? f.tds_rate })); }} /></div>
            </div>
            <div><Label className="text-[10px]">Rate %</Label><Input type="number" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: Number(e.target.value) })} className="rounded-sm h-8 mt-1 text-right tabular-nums" data-testid={`${kind}-tds-rate`} /></div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" onClick={() => { setOpenBill(false); setOpenPO(false); }} className="rounded-sm">Cancel</Button>
        {kind === "bill" && (
          <Button variant="outline" className="rounded-sm" data-testid="bill-save-draft" onClick={async () => {
            if (!form.vendor_id) return toast.error("Select a vendor");
            try {
              await saveDraft("purchase_bill", form, form, draftId);
              setDraftId(null); setOpenBill(false); setDraftsKey((k) => k + 1);
              setForm((f) => ({ ...f, vendor_id: "", vendor_bill_number: "", lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] }));
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); }
          }}>{draftId ? "Update Draft" : "Save as Draft"}</Button>
        )}
        <Button onClick={() => submit(kind)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid={`${kind}-submit`}>{kind === "bill" ? "Post Bill" : "Create PO"}</Button>
      </div>
      <div className="mt-4"><StagedAttachments ref={kind === "bill" ? stagedBillRef : stagedPORef} label={`Attach ${kind === "bill" ? "bill" : "PO"} supporting docs (uploads on save)`} /></div>
    </div>
  );

  // PO Terms panel — appears inside the New PO drawer above the submit
  // buttons via the {kind === "po" && <POTermsPanel />} slot below.
  const IncoTerms2020 = ["EXW", "FCA", "CPT", "CIP", "DAP", "DPU", "DDP", "FAS", "FOB", "CFR", "CIF"];
  const PaymentModes = ["Advance", "Net-15", "Net-30", "Net-45", "Net-60", "Net-90", "PDC", "LC", "Bank Guarantee"];
  const POTermsPanel = () => (
    <div className="mt-6 border rounded-sm p-4 bg-muted/30 space-y-3" data-testid="po-terms-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// PO commercial terms</div>
          <div className="text-sm text-muted-foreground">Feeds the nightly Violation Watchdog</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px]">Lead time (days)</Label>
          <Input type="number" value={form.lead_time_days || ""} onChange={(e) => setForm({ ...form, lead_time_days: Number(e.target.value) })}
                   className="rounded-sm h-8 mt-1 tabular-nums" data-testid="po-lead-time" placeholder="14" />
        </div>
        <div>
          <Label className="text-[10px]">INCOTERMS 2020</Label>
          <Select value={form.incoterm || ""} onValueChange={(v) => setForm({ ...form, incoterm: v })}>
            <SelectTrigger className="rounded-sm h-8 mt-1" data-testid="po-incoterm"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{IncoTerms2020.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Payment mode</Label>
          <Select value={form.payment_mode || ""} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
            <SelectTrigger className="rounded-sm h-8 mt-1" data-testid="po-payment-mode"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PaymentModes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Advance %</Label>
          <Input type="number" value={form.advance_pct || ""} onChange={(e) => setForm({ ...form, advance_pct: Number(e.target.value) })}
                   className="rounded-sm h-8 mt-1 tabular-nums" data-testid="po-advance-pct" placeholder="0" />
        </div>
        <div>
          <Label className="text-[10px]">Warranty (days)</Label>
          <Input type="number" value={form.warranty_days || ""} onChange={(e) => setForm({ ...form, warranty_days: Number(e.target.value) })}
                   className="rounded-sm h-8 mt-1 tabular-nums" data-testid="po-warranty" placeholder="365" />
        </div>
        <div>
          <Label className="text-[10px]">Due date (auto-computed)</Label>
          <Input readOnly value={form.date && form.lead_time_days ? new Date(new Date(form.date).getTime() + Number(form.lead_time_days) * 86400e3).toISOString().slice(0, 10) : ""}
                   className="rounded-sm h-8 mt-1 tabular-nums bg-muted" data-testid="po-due-date" />
        </div>
      </div>
      <div>
        <Label className="text-[10px]">Extra conditions (one per line)</Label>
        <textarea value={(form.extra_conditions || []).join("\n")}
                    onChange={(e) => setForm({ ...form, extra_conditions: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    rows={3} className="w-full rounded-sm border bg-background text-sm p-2 mt-1"
                    placeholder="e.g. Delivery at Plot #A-12 · Test certificate mandatory · Freight pre-paid"
                    data-testid="po-extra-conditions" />
      </div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="purchases-page">
      <DraftsPanel docType="purchase_bill" refreshKey={draftsKey} onPosted={() => load()}
        onOpen={(d) => { setForm(d.ui); setDraftId(d.id); setOpenBill(true); }} />
      <AutoBookPromoteBanner />
      <PageHeader
        eyebrow="Purchases"
        title="Purchase Orders & Bills"
        description="Track vendor spend, approvals and GST input credit."
        actions={
          <>
            <SendReportButton reportLabel="Purchase Register" defaultSubject="Purchase bills summary — Zenith 360" />
            <Button variant="outline" className="rounded-sm" data-testid="purchases-export-xlsx"
                      onClick={() => exportXlsx({
                        filename: `purchases_${new Date().toISOString().slice(0,10)}.xlsx`,
                        sheetName: "Purchase Bills",
                        columns: [
                          { key: "bill_number",         label: "Bill #" },
                          { key: "date",                 label: "Date", format: "date" },
                          { key: "vendor_name",         label: "Vendor" },
                          { key: "vendor_bill_number",  label: "Vendor Bill #" },
                          { key: "entity_gstin",        label: "Entity GSTIN" },
                          { key: "subtotal",            label: "Subtotal" },
                          { key: "tax_total",           label: "GST" },
                          { key: "grand_total",         label: "Total" },
                          { key: "balance_due",         label: "Balance Due" },
                        ],
                        rows: bills.filter((b) => !topGstinFilter || !b.entity_gstin || b.entity_gstin === topGstinFilter),
                      })}>Export XLSX</Button>
            <Button onClick={() => setOpenPO(true)} variant="outline" className="rounded-sm" data-testid="new-po-btn">+ Purchase Order</Button>
            <Button onClick={() => setOpenBill(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-bill-btn">+ Bill</Button>
          </>
        }
      />

      <div className="card-sharp p-3 flex items-center gap-3">
        <GstinSelector value={{ gstin: topGstinFilter }}
                          onChange={(g) => setTopGstinFilter(g?.gstin || "")}
                          label="GSTIN Scope" testid="purchases-top-gstin-filter" />
        <div className="text-xs text-muted-foreground ml-auto">
          {bills.filter((b) => !topGstinFilter || b.entity_gstin === topGstinFilter).length} of {bills.length} bills
        </div>
      </div>

      <Tabs defaultValue="bills">
        <TabsList className="rounded-sm">
          <TabsTrigger value="bills" className="rounded-sm">Bills</TabsTrigger>
          <TabsTrigger value="pos" className="rounded-sm">Purchase Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="bills" className="mt-4">
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Date</th><th>Bill #</th><th>Vendor</th><th>Vendor Bill #</th><th className="text-xs">Entity GSTIN</th><th className="text-right">Subtotal</th><th className="text-right">GST</th><th className="text-right">Total</th><th className="text-right">Due</th><th></th></tr></thead>
              <tbody>
                {bills.length === 0 && <tr><td colSpan="10" className="text-center text-muted-foreground py-8">No bills yet</td></tr>}
                {bills.filter((b) => !topGstinFilter || !b.entity_gstin || b.entity_gstin === topGstinFilter).map((b) => (
                  <tr key={b.id} onClick={() => setOpenBillDetail(b)} className="cursor-pointer" data-testid={`bill-row-${b.bill_number}`}>
                    <td className="text-xs">{fmtDate(b.date)}</td>
                    <td className="font-mono text-xs">
                      {b.bill_number}
                      {b.status === "cancelled" && <span className="ml-1 text-[9px] font-bold text-red-600" data-testid={`bill-cancelled-${b.bill_number}`}>CANCELLED</span>}
                      {b.source_ref && <div className="text-[10px] text-muted-foreground normal-case">from {b.source_type === "purchase_order" ? "PO" : "GRN"} {b.source_ref}</div>}
                    </td>
                    <td>{b.vendor_name}</td>
                    <td className="text-xs font-mono text-muted-foreground">{b.vendor_bill_number || "—"}</td>
                    <td className="text-xs font-mono text-muted-foreground">{b.entity_gstin || "—"}</td>
                    <td className="text-right tabular-nums">{fmtINR(b.subtotal)}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{fmtINR(b.tax_total)}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(b.grand_total)}</td>
                    <td className="text-right tabular-nums">{fmtINR(b.balance_due)}</td>
                    <td className="text-right space-x-1">
                      <AttachButton docType="purchase_bill" docId={b.id} docNumber={b.bill_number} />
                      {b.balance_due > 0.01 && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-blue-700 hover:text-blue-900"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const remark = window.prompt("Payment request note (optional):", "");
                                    if (remark === null) return;
                                    try {
                                      await api.post(`/payment-sheet/request-from-bill/${b.id}`, { remark: remark || "", priority: "normal" });
                                      toast.success("Added to Payment Sheet");
                                    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                                  }} data-testid={`bill-request-payment-${b.bill_number}`}>
                          <Send className="w-3 h-3" /> Request Payment
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); downloadPDF("bill", b.id); }} data-testid={`bill-pdf-${b.bill_number}`}><Download className="w-3 h-3" /> PDF</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="pos" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="po-export-xlsx"
                      onClick={() => exportXlsx({
                        filename: `purchase_orders_${new Date().toISOString().slice(0,10)}.xlsx`,
                        sheetName: "Purchase Orders",
                        columns: [
                          { key: "po_number",         label: "PO #" },
                          { key: "date",              label: "Date", format: "date" },
                          { key: "vendor_name",       label: "Vendor" },
                          { key: "grand_total",       label: "Total" },
                          { key: "approval_status",   label: "Approval" },
                          { key: "status",            label: "Status" },
                          { key: "lead_time_days",    label: "Lead Time (days)" },
                          { key: "incoterm",          label: "INCOTERMS" },
                          { key: "payment_mode",      label: "Payment Mode" },
                          { key: "advance_pct",       label: "Advance %" },
                          { key: "warranty_days",     label: "Warranty (days)" },
                          { key: "extra_conditions_str", label: "Extra Conditions" },
                        ],
                        rows: pos.map((p) => ({
                          ...p,
                          extra_conditions_str: (p.extra_conditions || []).join(" · "),
                        })),
                      })}>Export POs XLSX</Button>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Date</th><th>PO #</th><th>Vendor</th><th className="text-right">Total</th><th>Approval</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {pos.length === 0 && <tr><td colSpan="7" className="text-center text-muted-foreground py-8">No POs yet</td></tr>}
                {pos.map((p) => (
                  <tr key={p.id} onClick={() => setOpenPoDetail(p)} className="cursor-pointer" data-testid={`po-row-${p.po_number}`}>
                    <td className="text-xs">{fmtDate(p.date)}</td>
                    <td className="font-mono text-xs">
                      {p.po_number}
                      {p.dsc_signed && <span className="ml-1 text-emerald-600" title={`DSC signed by ${p.dsc_signer_name || "user"}`}>🔒</span>}
                    </td>
                    <td>{p.vendor_name}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(p.grand_total)}</td>
                    <td><span className={`text-[10px] font-mono uppercase tracking-widest ${p.approval_status === "approved" ? "text-emerald-600" : p.approval_status === "pending" ? "text-warning" : "text-muted-foreground"}`}>{(p.approval_status || "—").replace("_", " ")}</span></td>
                    <td><span className="text-[10px] font-mono uppercase tracking-widest">{p.status}</span></td>
                    <td className="text-right flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <AttachButton docType="purchase_order" docId={p.id} docNumber={p.po_number} />
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => downloadPDF("po", p.id)} data-testid={`po-pdf-${p.po_number}`}><Download className="w-3 h-3" /> PDF</Button>
                      {p.approval_status === "pending" && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => approvePO(p.id)} data-testid={`approve-po-${p.po_number}`}>Approve</Button>}
                      {p.status === "open" && <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => convertPOtoBill(p)} data-testid={`convert-po-bill-${p.po_number}`}>→ Bill <ArrowRight className="w-3 h-3" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={openBill} onOpenChange={(o) => { setOpenBill(o); if (!o && draftId) { setDraftId(null); setForm((f) => ({ ...f, vendor_id: "", vendor_bill_number: "", lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] })); } }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl rounded-none overflow-y-auto" data-testid="new-bill-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading">New Purchase Bill</SheetTitle>
            <div className="pt-2">
              <GstinSelector
                value={{ gstin: form.entity_gstin }}
                onChange={(g) => setForm({ ...form, entity_gstin: g?.gstin || "" })}
                label="Received under GSTIN"
                showLegalName
                testid="purchase-entity-gstin"
              />
            </div>
          </SheetHeader>
          {Form({ kind: "bill" })}
        </SheetContent>
      </Sheet>
      <Sheet open={openPO} onOpenChange={setOpenPO}>
        <SheetContent side="right" className="w-full sm:max-w-4xl rounded-none overflow-y-auto" data-testid="new-po-drawer">
          <SheetHeader><SheetTitle className="font-heading">New Purchase Order</SheetTitle></SheetHeader>
          {Form({ kind: "po" })}
          <POTermsPanel />
        </SheetContent>
      </Sheet>

      <Sheet open={!!openBillDetail} onOpenChange={(o) => !o && setOpenBillDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto space-y-4">
          <SheetHeader><SheetTitle className="font-heading">Bill {openBillDetail?.bill_number}</SheetTitle></SheetHeader>
          {openBillDetail?.status === "cancelled" && (
            <div className="border-2 border-red-600/60 text-red-600 rounded-sm p-2 text-xs font-medium" data-testid="bill-cancelled-banner">
              <div className="text-lg font-black tracking-widest">CANCELLED</div>
              {openBillDetail.cancel_reason} · by {openBillDetail.cancelled_by_name} on {fmtDate(openBillDetail.cancelled_at)}
            </div>
          )}
          {openBillDetail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Date</span><div className="font-medium">{fmtDate(openBillDetail.date)}</div></div>
                <div><span className="text-muted-foreground">Vendor</span><div className="font-medium">{openBillDetail.vendor_name}</div></div>
                <div><span className="text-muted-foreground">Total</span><div className="font-mono">{fmtINR(openBillDetail.grand_total)}</div></div>
                <div><span className="text-muted-foreground">Balance due</span><div className="font-mono">{fmtINR(openBillDetail.balance_due)}</div></div>
                {openBillDetail.tds_amount > 0 && <div className="col-span-2"><span className="text-muted-foreground">TDS deducted</span><div className="font-mono text-accent">{fmtINR(openBillDetail.tds_amount)} @ {openBillDetail.tds_rate}% u/s {openBillDetail.tds_section}</div></div>}
              </div>
              <Attachments docType="purchase_bill" docId={openBillDetail.id} />
              {canCancelBill && openBillDetail.status !== "cancelled" && !(Number(openBillDetail.paid) > 0) && (
                <Button variant="outline" onClick={() => setShowBillCancel(true)} className="rounded-sm text-red-600 border-red-300 hover:bg-red-50" data-testid="bill-cancel-btn">Cancel bill</Button>
              )}
              <CancelInvoiceDialog kind="bill" invoice={openBillDetail} open={showBillCancel} onOpenChange={setShowBillCancel}
                onDone={async () => { await load(); const r = await api.get("/purchase/bills"); setOpenBillDetail(r.data.find((x) => x.id === openBillDetail.id) || null); }} />
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!openPoDetail} onOpenChange={(o) => !o && setOpenPoDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto space-y-4" data-testid="po-detail-drawer">
          <SheetHeader><SheetTitle className="font-heading">Purchase Order {openPoDetail?.po_number}</SheetTitle></SheetHeader>
          {openPoDetail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Date</span><div className="font-medium">{fmtDate(openPoDetail.date)}</div></div>
                <div><span className="text-muted-foreground">Vendor</span><div className="font-medium">{openPoDetail.vendor_name}</div></div>
                <div><span className="text-muted-foreground">Total</span><div className="font-mono">{fmtINR(openPoDetail.grand_total)}</div></div>
                <div><span className="text-muted-foreground">Approval</span><div className="font-mono uppercase text-[10px]">{openPoDetail.approval_status?.replace("_", " ")}</div></div>
                <div><span className="text-muted-foreground">Status</span><div className="font-mono uppercase text-[10px]">{openPoDetail.status}</div></div>
              </div>
              <Attachments docType="purchase_order" docId={openPoDetail.id} />
              <DSCSigningPanel
                docType="purchase_order"
                docId={openPoDetail.id}
                downloadUnsignedUrl={`${API}/purchase/orders/${openPoDetail.id}/pdf`}
                downloadUnsignedLabel="Download unsigned PO PDF"
                onSigned={() => load()}
              />
              <ESignButton
                pdfPath={`/purchase/orders/${openPoDetail.id}/pdf`}
                fileName={`PO-${openPoDetail.po_number || openPoDetail.id}.pdf`}
                docType="purchase_order" docRef={openPoDetail.id}
                title={`PO ${openPoDetail.po_number || ""}`}
                testId="po-esign-btn"
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* RCM / Sec 17(5) compliance advisory — fires on Save of a bill */}
      <RcmAdvisoryModal
        billId={advisoryBillId}
        open={!!advisoryBillId}
        onOpenChange={(v) => !v && setAdvisoryBillId(null)}
        onDone={() => load()}
      />
    </div>
  );
}
